import { useEffect, useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { PlanStep } from './plan-step';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Button } from '@/new/components/shadcn/button';
import { ParameterValue, PlanStage } from '@/types';

interface PlanDrawerProps {
  loadingPlan: boolean;
  executePlan: (plan: PlanStage[]) => void;
  updatePlan: (plan: PlanStage[], step: number, parameterName: string, newValue: ParameterValue) => void;
  planStage: Record<string, string>;
  plan: PlanStage[];
  executing: boolean;
  editable: boolean;
  cancelPlan: () => void;
}

const PlanDrawer = ({
  loadingPlan,
  updatePlan,
  executePlan,
  planStage,
  plan,
  executing,
  editable,
  cancelPlan,
}: PlanDrawerProps) => {
  const container = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Find the index of the step that's currently in-progress
    const inProgressIndex = plan.findIndex((step) => planStage[step.name] === 'in-progress');
    if (inProgressIndex !== -1) {
      const stepElements = containerRef.current?.getElementsByClassName('plan-step');
      if (stepElements?.[inProgressIndex]) {
        const container = containerRef.current;
        const element = stepElements[inProgressIndex];

        // Calculate the scroll position to center the element
        const elementTop = (element as HTMLElement).offsetTop;
        const elementHeight = element.clientHeight;
        const containerHeight = container.clientHeight;
        const scrollTo = elementTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: scrollTo,
          behavior: 'smooth',
        });
      }
    }
  }, [planStage, plan]);

  return (
    <AnimatePresence>
      <motion.div
        className="h-full w-2/5 max-w-[400px] border-r"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-10 w-full items-center gap-2 border-b px-4 text-lg font-medium">
            <span className="iconify lucide--list-check h-4 w-4" />
            Plan
          </div>

          <div className="flex flex-1 flex-col justify-between">
            {loadingPlan ? (
              <div className="mx-4 mt-10 flex h-[400px] flex-col items-center justify-center gap-4 rounded-lg border-2">
                <p className="text-sm text-gray-500">Building plan...</p>
                <span className="iconify lucide--loader-circle h-3 w-3 animate-spin" />
              </div>
            ) : (
              <div className="h-full">
                <p className="px-4 pt-3 pb-1 text-sm text-gray-800">Kim will execute the following plan:</p>
                <div
                  ref={containerRef}
                  className={`mx-4 my-1 overflow-y-auto rounded-lg border p-4 text-sm text-gray-700 ${
                    executing ? 'pointer-events-auto select-none [&_*]:pointer-events-none' : ''
                  }`}
                >
                  <motion.div
                    className="relative flex flex-col"
                    variants={container}
                    initial="hidden"
                    animate="visible"
                  >
                    {/* Continuous vertical line */}
                    <div className="absolute top-4 bottom-2 left-[0.7rem] z-0 w-0 border-l-2 border-gray-300"></div>
                    {plan.map((step, index) => (
                      <PlanStep
                        key={index}
                        step={step}
                        index={index}
                        executing={executing}
                        planStage={planStage}
                        updatePlan={(parameterName: string, newValue: ParameterValue) =>
                          updatePlan(plan, index, parameterName, newValue)
                        }
                      />
                    ))}
                  </motion.div>
                </div>
              </div>
            )}
            {editable ? (
              <div className="h-28 px-4 pt-1 pb-4">
                <div className="py-4 text-xs text-gray-500">
                  You can alter any highlighted <span className="rounded bg-blue-100 px-1 text-gray-600">options</span>{' '}
                  of a plan.
                </div>
                <div className="flex flex-row justify-between gap-2">
                  <Button onClick={() => cancelPlan()} disabled={executing || loadingPlan} variant="destructive">
                    Cancel
                    <span className="iconify lucide--x h-4 w-4" />
                  </Button>
                  <Button variant={'primary'} onClick={() => executePlan(plan)} disabled={executing || loadingPlan}>
                    Execute Plan
                    <span className="iconify lucide--wand-sparkles h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface PlanPageProps {
  questionValue: string;
  loading: boolean;
  messages: string[];
}

const PlanPage = ({ questionValue, loading, messages }: PlanPageProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const processMessages = (messages: string[]): React.ReactNode => {
    if (!messages || messages.length === 0) return '';

    return messages.map((message, messageIndex) => {
      const paragraphs = message.split('<br>');

      return (
        <motion.div
          key={messageIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: messageIndex * 0.1 }}
          className="flex items-center gap-3 rounded-md p-1"
        >
          <span className="iconify lucide--circle h-2 w-2 text-teal-500"></span>

          <div className="min-w-0 flex-1 space-y-1 border-b border-dashed">
            {paragraphs.map((paragraph, index) => {
              // Trim leading/trailing commas and spaces
              const trimmedParagraph = paragraph.trim().replace(/^,+|,+$/g, '');
              if (!trimmedParagraph) return null;

              // Replace numbers with bold numbers using JSX
              const parts = trimmedParagraph.split(/(\d+)/);
              return (
                <p key={index} className="text-xs leading-relaxed text-gray-700">
                  {parts.map((part, partIndex) =>
                    isNaN(Number(part)) ? (
                      part
                    ) : (
                      <strong key={partIndex} className="font-semibold text-gray-900">
                        {part}
                      </strong>
                    ),
                  )}
                </p>
              );
            })}
          </div>
        </motion.div>
      );
    });
  };

  return (
    <div className="mx-10 min-w-0 flex-1">
      {/* Question Header */}
      <div className="mt-6 mb-12 text-gray-700">
        <h1 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-700">
          <span className="iconify lucide--message-circle-question text-brandSecondary/60 text-lg" />
          Question
        </h1>
        <div className="border-brandSecondary/30 max-h-[150px] overflow-y-auto rounded-md border-l-2 bg-gray-50/50 px-4 py-3 text-sm text-gray-600">
          <div className="prose prose-sm max-w-none">
            <p className="leading-relaxed text-gray-700">{capitalizeFirstLetter(questionValue)}</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-6">
          {/* Execution Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium text-gray-800">
              <span className="iconify lucide--rocket h-4 w-4 text-teal-500" />
              Executing Plan
            </div>
            <AnimatePresence>
              <motion.div className="max-h-96 overflow-y-auto rounded-md border border-gray-100 bg-gray-50/50 p-4">
                <div className="space-y-3">
                  {processMessages(messages)}
                  <div ref={messagesEndRef} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export { PlanDrawer, PlanPage };
